import { useMemo, useState } from 'react';

import { useBasket } from '~/features/basket/hooks/useBasket';
import UseBasketPaneState, { BasketPaneContentType } from '~/features/basket/hooks/useBasketPaneState';
import { useAddProductTracking } from '~/features/tracking/hooks/useAddProductTracking';
import { IBreadcrumb, IP90BasketUspItem, IVariationDetails } from '~/lib/data-contract';
import { AddToBasket } from '~/shared/components/AddToBasket/AddToBasket';
import { Dialog } from '~/shared/components/Dialog/Dialog';
import { DialogContent } from '~/shared/components/Dialog/DialogContent';
import { RawHtml } from '~/shared/components/RawHtml/RawHtml';
import { useMarket } from '~/shared/utils/market/hooks/useMarket';
import { useTranslation } from '~/shared/utils/translation/hooks/useTranslation';

import { ProductPicker } from './../ProductPicker/ProductPicker';
import { ProductDetailsBreadcrumb } from './components/ProductDetailsBreadcrumb/ProductDetailsBreadcrumb';
import { ProductDetailsCarousel } from './components/ProductDetailsCarousel/ProductDetailsCarousel';
import { ProductDetailsStamp } from './components/ProductDetailsStamp/ProductDetailsStamp';
import { ProductDetailsSummary } from './components/ProductDetailsSummary/ProductDetailsSummary';
import { ProductDetailsUsps } from './components/ProductDetailsUsps/ProductDetailsUsps';
import styles from './productDetails.module.css';

import { useImageAltText } from '../../hooks/useImageAltText';
import { StockStatusDetermined } from '../../utils/stockState/useStockStatusDetermined';

export type ProductDetailsProps = {
	variation: IVariationDetails;
	opacity: number;
	stockStatus: StockStatusDetermined;
	usps?: IP90BasketUspItem[];
	deliveryUrl?: string;
	categoryBreadcrumb?: IBreadcrumb;
};

export const ProductDetails = ({ variation, opacity, stockStatus, usps, deliveryUrl, categoryBreadcrumb }: ProductDetailsProps) => {
	const [productGuideOpen, setProductGuideOpen] = useState(false);

	const { translate } = useTranslation();
	const { market, culture } = useMarket();
	const { addItem } = useBasket();
	const { openDialog } = UseBasketPaneState();
	const trackAddProduct = useAddProductTracking(variation);
	const basketPaneContentType = useMemo((): BasketPaneContentType => {
		if (variation.manualOrderItem) {
			return 'manualOrder';
		}

		if (stockStatus.stockState === 'preorder') {
			return 'preorder';
		}

		return 'normal';
	}, [variation.manualOrderItem, stockStatus]);

	const handleConfirm = (quantity: number) => {
		addItem(
			{ variation, quantity },
			{
				onSuccess: ({ basket }) => {
					trackAddProduct(quantity, basket);
					openDialog(basketPaneContentType);
				},
			},
		);
	};

	const images = [...variation.images, ...variation.moodImages];
	const imageAltText = useImageAltText(variation);

	return (
		<>
			<div className={styles.root}>
				<div className={styles.container}>
					<ProductDetailsBreadcrumb
						itemMainType={variation.categoryLink}
						categoryBreadcrumb={categoryBreadcrumb}
					/>
					<div className={styles.carouselContainer}>
						{variation.stampImage ? (
							<ProductDetailsStamp
								stamp={variation.stampImage}
								link={variation.stampLink}
							/>
						) : null}
						{images.length ? (
							<ProductDetailsCarousel
								key={variation.id}
								opacity={opacity}
								images={images}
								imageAltText={imageAltText}
							/>
						) : null}
					</div>
					<aside className={styles.aside}>
						<ProductDetailsSummary
							variation={variation}
							deliveryUrl={deliveryUrl}
						/>
						<ProductPicker variation={variation} />

						{stockStatus.availabilityDate ? (
							<span className={styles.preOrderAvailable}>
								{translate('product.preorder.available', {
									date: stockStatus.availabilityDate.toLocaleDateString(`${culture}-${market}`, {
										year: 'numeric',
										month: 'long',
										day: 'numeric',
									}),
								})}
							</span>
						) : null}

						<AddToBasket
							isStockAvailable={stockStatus.stockAvailable}
							stockState={stockStatus.stockState}
							isManualOrder={variation.manualOrderItem}
							onConfirm={handleConfirm}
							isPdp={true}
						/>

						{variation.productGuide?.buttonLabel ? (
							<button
								className={styles.productGuideButton}
								onClick={() => setProductGuideOpen((prevProductGuideOpen) => !prevProductGuideOpen)}
							>
								{variation.productGuide.buttonLabel}
							</button>
						) : null}
						{usps && usps.length ? <ProductDetailsUsps usps={usps} /> : null}
					</aside>
				</div>
			</div>

			{variation.productGuide?.content ? (
				<Dialog
					open={productGuideOpen}
					onOpenChange={setProductGuideOpen}
				>
					<DialogContent>
						<RawHtml html={variation.productGuide.content.html} />
					</DialogContent>
				</Dialog>
			) : null}
		</>
	);
};
